'use client';

import { CloseButton, Popover, PopoverButton, PopoverPanel, Transition } from '@headlessui/react';
import { Badge, Button, ControlledInput, Label, Typography } from 'components';
import { SortOrderEnum, TransactionSortAttributeEnum } from 'gql/graphql';
import { CloseIcon, FiltersIcon } from 'lib/svgs/Icons';
import { useTranslations } from 'next-intl';
import { KeysEnum } from 'types/KeysEnum';
import { cn } from 'utils/cn';
import { useTransactionsContext } from '../_context/TransactionsContext';

export default function FilterSortTransactions() {
  const t = useTranslations();

  const {
    control,
    handleClear,
    columnsKeys,
    columnsKeysFiltered,
    TRANSACTION_KEYS_MAP,
    handleOnClickBadge
  } = useTransactionsContext();

  const optionsSortAttribute = [
    {
      label: t(
        `billing.transactions.filters.sort_attributes.${TransactionSortAttributeEnum.Title}` as any
      ),
      value: TransactionSortAttributeEnum.Title
    },
    {
      label: t(
        `billing.transactions.filters.sort_attributes.${TransactionSortAttributeEnum.Id}` as any
      ),
      value: TransactionSortAttributeEnum.Id
    },
    {
      label: t(
        `billing.transactions.filters.sort_attributes.${TransactionSortAttributeEnum.Quality}` as any
      ),
      value: TransactionSortAttributeEnum.Quality
    },
    {
      label: t(
        `billing.transactions.filters.sort_attributes.${TransactionSortAttributeEnum.LanguagePair}` as any
      ),
      value: TransactionSortAttributeEnum.LanguagePair
    },
    {
      label: t(
        `billing.transactions.filters.sort_attributes.${TransactionSortAttributeEnum.CreatedAt}` as any
      ),
      value: TransactionSortAttributeEnum.CreatedAt
    },
    {
      label: t(
        `billing.transactions.filters.sort_attributes.${TransactionSortAttributeEnum.TransactionType}` as any
      ),
      value: TransactionSortAttributeEnum.TransactionType
    },
    {
      label: t(
        `billing.transactions.filters.sort_attributes.${TransactionSortAttributeEnum.InvoiceId}` as any
      ),
      value: TransactionSortAttributeEnum.InvoiceId
    },
    {
      label: t(
        `billing.transactions.filters.sort_attributes.${TransactionSortAttributeEnum.Amount}` as any
      ),
      value: TransactionSortAttributeEnum.Amount
    },
    {
      label: t(
        `billing.transactions.filters.sort_attributes.${TransactionSortAttributeEnum.PoCode}` as any
      ),
      value: TransactionSortAttributeEnum.PoCode
    }
  ];

  const optionsSortOrder = [
    {
      label: t(`filters.sort_order.${SortOrderEnum.Asc}` as any),
      value: SortOrderEnum.Asc
    },
    {
      label: t(`filters.sort_order.${SortOrderEnum.Desc}` as any),
      value: SortOrderEnum.Desc
    }
  ];

  return (
    <div className="flex flex-1 justify-end gap-4">
      <Popover>
        <PopoverButton as="div" className="outline-none">
          <div
            data-tooltip-id="tolq-tooltip"
            data-tooltip-content={t('tooltips.customization_sorting')}
          >
            <Button
              variant="icon"
              color="none"
              colorIcon="blue-dark"
              startAdornment={<FiltersIcon className="scale-[0.9] !text-tertiary-200" />}
              className="!h-10 w-10 !rounded-3xl !border-1 !border-primary-blue-100 !px-[6px] !outline-none"
            />
          </div>
        </PopoverButton>
        <Transition
          enter="transition ease-out duration-75"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <PopoverPanel
            className={cn(
              'absolute right-[56px] z-10 mt-3 origin-top-right [--anchor-gap:var(--spacing-1)]',
              'w-96 rounded-md bg-white p-4 pt-3 shadow-custom'
            )}
          >
            <div className="mb-2">
              <Typography variant="medium3" className="text-left text-tertiary-100">
                {t('labels.customization')}
              </Typography>
            </div>
            <CloseButton className="absolute right-1 top-0 z-[1000]">
              <Button
                size="small"
                variant="icon"
                color="neutral"
                className="!w-8 rounded-full"
                startAdornment={<CloseIcon className="scale-[.9]" />}
              />
            </CloseButton>
            <div>
              <Label className="mt-0">{t('labels.sort_items')}</Label>
              <div className="relative flex gap-2">
                <ControlledInput
                  control={control}
                  type="select"
                  name={KeysEnum.SortAttribute}
                  classNameParent="w-full !m-0"
                  options={optionsSortAttribute}
                  defaultOption={optionsSortAttribute.at(3)}
                />
                <ControlledInput
                  control={control}
                  type="select"
                  name={KeysEnum.SortOrder}
                  classNameParent="w-full !m-0"
                  options={optionsSortOrder}
                  defaultOption={optionsSortOrder.at(1)}
                />
              </div>
            </div>
            <div>
              <div className="flex flex-1 flex-col justify-between">
                <Label className="mt-0">{t('labels.display_properties')}</Label>
                <div className="flex flex-wrap gap-2">
                  {columnsKeys.map((key) => {
                    const isVisible = (columnsKeysFiltered as string[])?.includes(key as string);
                    return (
                      <Badge
                        key={key}
                        className={cn(
                          'relative h-6 cursor-pointer',
                          isVisible ? '!bg-primary-blue-900' : '!bg-tertiary-800'
                        )}
                        classNameTypography={cn(
                          'ml-1 !text-xs',
                          isVisible ? 'text-primary-blue-100' : 'text-tertiary-100'
                        )}
                        onClick={handleOnClickBadge(key as string)}
                      >
                        {t(
                          `billing.transactions.table.${
                            TRANSACTION_KEYS_MAP[key as keyof typeof TRANSACTION_KEYS_MAP]
                          }` as any
                        )}
                      </Badge>
                    );
                  })}
                </div>
              </div>
            </div>
            <hr className="my-2 mt-4 border !border-t-0 border-tertiary-600" />
            <div>
              <div className="flex flex-1 justify-end">
                <Button
                  variant="text"
                  size="small"
                  className="!p-0 !text-xs"
                  label={t('buttons.reset')}
                  onClick={handleClear}
                />
              </div>
            </div>
          </PopoverPanel>
        </Transition>
      </Popover>
    </div>
  );
}
